/**
 * @voltxampere/fx — Voltage field interpolation + color lerp (Sprint 30-1)
 *
 * Two pure-math primitives the canvas overlay layer (apps/web) will use to
 * paint Vision §E voltage heatmap halos + wire color gradients:
 *
 *   1. `interpolateVoltage(point, samples)` — Inverse-Distance Weighting
 *      (IDW^2) so an arbitrary canvas point gets a voltage estimate from
 *      the surrounding node samples. IDW is cheap, monotone, and degenerates
 *      sensibly when point ≈ sample (returns the sample voltage exactly).
 *      RBF (radial-basis-function) is the textbook alternative but adds an
 *      O(n²) solve per sample-set rebuild that we do not need for the
 *      typical educational circuit (≤20 nodes).
 *
 *   2. `voltageToColor(voltage, range)` — three-stop palette lerp using the
 *      Mockup §E canonical tokens (vfLow / vfMid / vfHigh). Returns RGBA in
 *      [0,255] / [0,1] so the canvas renderer can format it via
 *      `rgba(r,g,b,a)` without further conversion.
 *
 * IMPORT DISCIPLINE: This module touches no other package. The
 * NodeVoltageSample shape is local; apps/web's adapter constructs samples
 * from `circuit.nets[].id` + the engine's transient `nodeVoltages` map.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
/** Below this point-to-sample distance (world units squared) the helper
 * snaps to the sample's own voltage to avoid 1/0 division. */
const SNAP_DISTANCE_SQ = 1e-6;
/**
 * Inverse-Distance Weighting interpolator.
 *
 * Formula: V(p) = Σ wi·Vi / Σ wi where wi = 1/d^power.
 *
 * Edge cases:
 *  - empty samples array → 0 (caller-side gracefully renders no heatmap)
 *  - single sample → that sample's voltage (trivial degenerate)
 *  - point exactly on a sample → that sample's voltage (snap guard)
 *
 * The default `power = 2` matches Shepard's classic IDW²; smaller values
 * (e.g. 1) produce smoother fields, larger (e.g. 4) produce sharper
 * peaks around sample points. Sprint 30 sticks to 2 — visually balanced
 * for the typical 3-12 node circuit.
 */
export function interpolateVoltage(point, samples, power = 2) {
    if (samples.length === 0)
        return 0;
    if (samples.length === 1)
        return samples[0]?.voltage ?? 0;
    let weightedSum = 0;
    let weightTotal = 0;
    for (const sample of samples) {
        const dx = point.x - sample.position.x;
        const dy = point.y - sample.position.y;
        const distSq = dx * dx + dy * dy;
        if (distSq < SNAP_DISTANCE_SQ) {
            // Point coincides with this sample — return its voltage exactly.
            return sample.voltage;
        }
        const weight = 1 / Math.pow(distSq, power / 2);
        weightedSum += sample.voltage * weight;
        weightTotal += weight;
    }
    return weightTotal === 0 ? 0 : weightedSum / weightTotal;
}
/**
 * Compute the [min, max] voltage band across a sample set.
 *
 * Used to normalise voltages into the 3-stop palette. Empty input returns
 * `{ min: 0, max: 0 }` so the caller can render a no-op (palette lerp
 * collapses to vfLow when range is degenerate).
 */
export function voltageRange(samples) {
    if (samples.length === 0)
        return { min: 0, max: 0 };
    let min = Infinity;
    let max = -Infinity;
    for (const s of samples) {
        if (s.voltage < min)
            min = s.voltage;
        if (s.voltage > max)
            max = s.voltage;
    }
    return { min, max };
}
// Mockup §E canonical palette — Şefim 2026-05-02 notu.
const VF_LOW = { r: 0x28, g: 0x3c, b: 0x64, a: 0.2 }; // #283C64
const VF_MID = { r: 0x60, g: 0x82, b: 0xa8, a: 0.25 }; // #6082A8
const VF_HIGH = { r: 0xe8, g: 0xb3, b: 0x5c, a: 0.35 }; // #E8B35C
/**
 * Smoothly interpolate between two RGBA samples. `t` is clamped to [0,1].
 * Each channel lerped independently.
 */
function lerpRGBA(a, b, t) {
    const tt = t < 0 ? 0 : t > 1 ? 1 : t;
    return {
        r: Math.round(a.r + (b.r - a.r) * tt),
        g: Math.round(a.g + (b.g - a.g) * tt),
        b: Math.round(a.b + (b.b - a.b) * tt),
        a: a.a + (b.a - a.a) * tt,
    };
}
/**
 * Map a voltage value to an RGBA color via the 3-stop Mockup §E palette.
 *
 * Two-segment lerp for visual continuity:
 *   normalized ≤ 0.5 → lerp(vfLow, vfMid, normalized × 2)
 *   normalized > 0.5 → lerp(vfMid, vfHigh, (normalized − 0.5) × 2)
 *
 * `normalized = (voltage − range.min) / (range.max − range.min)`. When
 * `range.max === range.min` (single-voltage circuit, e.g. all-ground) the
 * function returns vfLow as a sensible default.
 */
export function voltageToColor(voltage, range) {
    const span = range.max - range.min;
    if (span === 0)
        return VF_LOW;
    const normalised = (voltage - range.min) / span;
    // Two-segment lerp through the three stops
    if (normalised <= 0.5) {
        return lerpRGBA(VF_LOW, VF_MID, normalised * 2);
    }
    return lerpRGBA(VF_MID, VF_HIGH, (normalised - 0.5) * 2);
}
/** Public re-export of the canonical palette tokens for renderer-side reuse. */
export const VOLTAGE_PALETTE = Object.freeze({
    low: VF_LOW,
    mid: VF_MID,
    high: VF_HIGH,
});
//# sourceMappingURL=voltage-field.js.map